import { ApiError, apiErrorMessage, useCreateJob, useSendJob } from "@vidario/api-client";
import { type JobCreateInput } from "@vidario/domain";
import { Breadcrumb, useToast } from "@vidario/ui";
import { useState, type ReactElement } from "react";
import { useNavigate } from "react-router";

import { JobForm } from "./JobForm";
import { NO_SEARCHES_MESSAGE, SearchQuotaHint, useSearchQuota } from "./SearchQuota";

/** MVP_requirements.md §4 Screen 2. Primary path is the auto-import
 * card (URL / PDF); manual form is the always-available fallback.
 * „Entwurf speichern" keeps the job a Draft, editable from
 * `/jobs/:jobId/edit`; „An Vidario senden" saves the Draft and starts its
 * search, which is what takes it out of Draft (#16). */
export function JobCreateRoute(): ReactElement {
  const navigate = useNavigate();
  const toast = useToast();
  // The backend's reasons per field, shown next to the fields (#22).
  const [serverErrors, setServerErrors] = useState<Record<string, string[]>>({});
  const onSaveError = (err: Error): void => {
    setServerErrors(err instanceof ApiError ? err.fieldErrors : {});
    toast.push({ kind: "error", title: "Fehler beim Speichern", message: err.message });
  };
  const createJob = useCreateJob();
  const sendJob = useSendJob();
  const { canStartSearch } = useSearchQuota();

  const saveDraft = (values: JobCreateInput): void => {
    createJob.mutate(
      { input: values },
      {
        onSuccess: (job) => {
          toast.push({
            kind: "success",
            title: "Entwurf gespeichert",
            message: `„${job.title}" wurde als Entwurf gespeichert.`,
          });
          void navigate("/dashboard");
        },
        onError: onSaveError,
      },
    );
  };

  const send = (values: JobCreateInput): void => {
    // Block upfront so a recruiter at 0 quota keeps the form as it is.
    if (!canStartSearch) {
      toast.push({ kind: "error", title: "Keine Suchen verfügbar", message: NO_SEARCHES_MESSAGE });
      return;
    }
    sendJob.mutate(
      { input: values },
      {
        onSuccess: (result) => {
          if (result.started) {
            toast.push({
              kind: "success",
              title: "An Vidario gesendet",
              message: `„${result.job.title}" ist live — die Kandidatensuche läuft.`,
            });
            void navigate(`/jobs/${result.job.id}/longlist`);
            return;
          }
          // The Draft exists now: continue on its edit form, so sending
          // again updates it instead of creating a second job.
          toast.push({
            kind: "error",
            title: "Suche konnte nicht gestartet werden",
            message: `${apiErrorMessage(result.error)} Die Stelle wurde als Entwurf gespeichert.`,
          });
          void navigate(`/jobs/${result.job.id}/edit`);
        },
        onError: onSaveError,
      },
    );
  };

  return (
    <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8 pb-20">
      <Breadcrumb
        className="mb-5"
        items={[
          { label: "Dashboard", onClick: () => void navigate("/dashboard") },
          { label: "Neue Stelle" },
        ]}
      />

      <div className="mb-8 flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-bold -tracking-[0.5px]">Neue Stelle anlegen</h1>
          <p className="mt-1.5 text-sm text-text-tertiary">
            Importiere eine bestehende Stellenausschreibung oder fülle die Felder manuell aus.
          </p>
          <SearchQuotaHint />
        </div>
      </div>

      <JobForm
        mode="create"
        submitting={createJob.isPending || sendJob.isPending}
        serverErrors={serverErrors}
        onCancel={() => void navigate("/dashboard")}
        onSubmit={(values, submit) => (submit ? send(values) : saveDraft(values))}
      />
    </main>
  );
}
